import React, { useState } from 'react';
import { ArrowPathIcon, ClockIcon } from '@heroicons/react/24/outline';
import type { SreInvestigateTarget } from '../types';
import {
  getSreInvestigationMode,
  isInvestigatableState,
  isSreInvestigationAvailable,
} from '../constants';
import { useTicketInvestigations } from '../hooks/useTicketInvestigations';
import { ensureSreFontsLoaded } from '../styles/theme';
import { normalizeSreStatus } from '../utils/sreStatusMessaging';
import SreInvestigatePanel from './SreInvestigatePanel';
import SreInvestigatedBadge from './SreInvestigatedBadge';
import SreInvestigationHistoryPanel from './SreInvestigationHistoryPanel';
import SreCaseStatusBadge from './SreCaseStatusBadge';

export interface SreInvestigateActionsProps extends SreInvestigateTarget {
  className?: string;
  onPosted?: () => void;
  forceShow?: boolean;
  autoStart?: boolean;
}

/**
 * Default ticket actions (T-022 / T-030 / T-031 / T-033 / T-035):
 * - Case SRE status badge + operator messaging when gate status is set
 * - No runs → "SRE Investigation"
 * - Completed run → green "Investigated" (opens latest)
 * - Multiple runs → History icon (opens list by default)
 * - Reinvestigate control when a prior run / cooldown skip exists (T-033)
 * - History list stays live across tabs/clients (T-035)
 */
const SreInvestigateActions: React.FC<SreInvestigateActionsProps> = ({
  className,
  onPosted,
  forceShow = false,
  autoStart = false,
  sreInvestigationStatus,
  sreDecisionReason,
  sreEvaluatedAt,
  ...target
}) => {
  const [workbenchOpen, setWorkbenchOpen] = useState(false);
  const [initialRunId, setInitialRunId] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [panelTriggerSource, setPanelTriggerSource] = useState<
    'pulse_ui' | 'reinvestigate'
  >('pulse_ui');
  const [panelAutoStart, setPanelAutoStart] = useState(autoStart);
  const mode = getSreInvestigationMode();
  const {
    runs,
    loading,
    error,
    investigated,
    latestCompleted,
    refresh,
    notifyHistoryChanged,
  } = useTicketInvestigations(target.ticketNumber);

  React.useEffect(() => {
    ensureSreFontsLoaded();
  }, []);

  if (!forceShow && !isSreInvestigationAvailable()) {
    return null;
  }

  if (!target.ticketNumber?.trim()) {
    return null;
  }

  const enabled = isInvestigatableState(target.ticketState);
  const showHistoryIcon = mode === 'live' && runs.length > 1;
  const statusNormalized = normalizeSreStatus(sreInvestigationStatus);
  const reasonNormalized = (sreDecisionReason || '').trim().toLowerCase();
  // Once the green "Investigated" badge is showing (a completed run exists in
  // history), hide the gate status badge/message entirely — a stale
  // skipped/cooldown gate decision from *before* that completed run must not
  // be shown alongside it. Only suppress for in-flight/terminal gate states
  // that would otherwise contradict "Investigated"; running/queued states
  // still surface even if a prior run completed (a fresh reinvestigation).
  const investigatedBadgeShown = mode === 'live' && investigated;
  const showCaseStatus =
    Boolean(statusNormalized) &&
    statusNormalized !== 'none' &&
    !(
      investigatedBadgeShown &&
      (statusNormalized === 'completed' ||
        statusNormalized === 'skipped' ||
        statusNormalized === 'not_available' ||
        statusNormalized === 'failed')
    );
  /** T-033: allow operators to force a new run after cooldown / prior completion. */
  const showReinvestigate =
    mode === 'live' &&
    enabled &&
    statusNormalized !== 'running' &&
    statusNormalized !== 'queued' &&
    statusNormalized !== 'pending_eval' &&
    (investigated ||
      statusNormalized === 'completed' ||
      statusNormalized === 'failed' ||
      statusNormalized === 'skipped' ||
      reasonNormalized === 'cooldown');

  const openWorkbench = (
    runId: string | null,
    options?: { reinvestigate?: boolean; autoStart?: boolean }
  ) => {
    setInitialRunId(runId);
    setPanelTriggerSource(options?.reinvestigate ? 'reinvestigate' : 'pulse_ui');
    setPanelAutoStart(
      Boolean(options?.autoStart || options?.reinvestigate || (autoStart && !runId))
    );
    setWorkbenchOpen(true);
  };

  const handlePosted = () => {
    notifyHistoryChanged();
    onPosted?.();
  };

  const handleCloseWorkbench = () => {
    setWorkbenchOpen(false);
    setInitialRunId(null);
    setPanelTriggerSource('pulse_ui');
    setPanelAutoStart(false);
    notifyHistoryChanged();
  };

  return (
    <div
      className={['inline-flex flex-wrap items-center gap-2', className]
        .filter(Boolean)
        .join(' ')}
      data-sre-investigation-actions
    >
      {showCaseStatus ? (
        <SreCaseStatusBadge
          status={sreInvestigationStatus}
          reason={sreDecisionReason}
          evaluatedAt={sreEvaluatedAt}
        />
      ) : null}

      {mode === 'live' && investigated ? (
        <SreInvestigatedBadge
          investigated
          loading={loading}
          latest={latestCompleted}
          runCount={runs.length}
          onClick={() => {
            const id = latestCompleted?.id || null;
            openWorkbench(id);
          }}
        />
      ) : mode === 'live' && loading ? (
        <SreInvestigatedBadge investigated={false} loading />
      ) : (
        <button
          type="button"
          onClick={() => openWorkbench(null)}
          disabled={!enabled}
          title={
            !enabled
              ? `Investigation is only available for active ticket states (current: ${target.ticketState || 'unknown'}).`
              : statusNormalized === 'not_available'
                ? 'No eligible use case yet — opening the workbench may still show evaluate results.'
                : statusNormalized === 'skipped' && sreDecisionReason
                  ? `Previously skipped (${sreDecisionReason}). You can still start a manual investigation if eligible.`
                  : undefined
          }
          aria-label={`Open SRE Investigation workbench for ${target.ticketNumber}`}
          className={[
            'group inline-flex items-center gap-2 rounded-lg',
            'border border-primary-200 bg-white',
            'px-3.5 py-2 text-sm font-semibold text-primary-700',
            'shadow-sm shadow-primary-600/5',
            'transition duration-150',
            'hover:border-primary-300 hover:bg-primary-50 hover:text-primary-900',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
            'disabled:cursor-not-allowed disabled:opacity-50',
          ].join(' ')}
        >
          <span>SRE Investigation</span>
        </button>
      )}

      {showReinvestigate ? (
        <button
          type="button"
          onClick={() =>
            openWorkbench(null, { reinvestigate: true, autoStart: true })
          }
          disabled={!enabled}
          title="Start a new investigation now (bypasses evaluate cooldown)"
          aria-label={`Reinvestigate ${target.ticketNumber}`}
          className={[
            'inline-flex items-center gap-1.5 rounded-lg',
            'border border-slate-200 bg-white',
            'px-3 py-2 text-sm font-semibold text-slate-700',
            'transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1',
            'disabled:cursor-not-allowed disabled:opacity-50',
          ].join(' ')}
        >
          <ArrowPathIcon className="h-4 w-4" aria-hidden />
          <span>Reinvestigate</span>
        </button>
      ) : null}

      {showHistoryIcon ? (
        <button
          type="button"
          onClick={() => setHistoryOpen(true)}
          title={`View ${runs.length} investigation runs`}
          aria-label="Open SRE investigation history"
          className={[
            'inline-flex items-center justify-center rounded-lg',
            'border border-slate-200 bg-white p-2 text-slate-600',
            'transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1',
          ].join(' ')}
        >
          <ClockIcon className="h-5 w-5" aria-hidden />
        </button>
      ) : null}

      <SreInvestigatePanel
        open={workbenchOpen}
        onClose={handleCloseWorkbench}
        target={target}
        onPosted={handlePosted}
        autoStart={panelAutoStart && !initialRunId}
        initialRunId={initialRunId}
        triggerSource={panelTriggerSource}
      />

      {mode === 'live' ? (
        <SreInvestigationHistoryPanel
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
          ticketNumber={target.ticketNumber}
          runs={runs}
          loading={loading}
          error={error}
          onRefresh={refresh}
          preferListView={runs.length > 1}
          onOpenRun={(runId) => {
            setHistoryOpen(false);
            openWorkbench(runId);
          }}
        />
      ) : null}
    </div>
  );
};

export default SreInvestigateActions;
